"use client";

import styled from "@emotion/styled";
import { useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";

export interface WcDateCountDownProps {
  deadline: string;
  labels: {
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
  };
  clockAmountFontSize?: string;
  clockUnitFontSize?: string;
  radius?: number;
  strokeWidth?: number;
  stroke?: string;
  disableSeconds?: boolean;
  disableMinutes?: boolean;
  disableHours?: boolean;
  disableDays?: boolean;
}

export const WcDateCountDown = (props: WcDateCountDownProps) => {
  const theme = useTheme();

  const Container = styled.div`
    .clock {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-content: center;
      margin: 0 auto;
    }
    .clock__display {
      position: relative;
    }
    .clock__text {
      align-items: center;
      display: flex;
      flex-direction: column;
      height: 100%;
      justify-content: center;
      position: absolute;
      text-align: center;
      top: 0;
      width: 100%;
    }
    .clock__amount {
      font-size: ${props.clockAmountFontSize ? props.clockAmountFontSize : "1.5rem"};
    }
    .clock__unit {
      font-size: ${props.clockUnitFontSize ? props.clockUnitFontSize : "0.75rem"};
    }
    .circle {
      transform: rotate(-90deg);
      transform-origin: 50% 50%;
      transition: stroke-dashoffset 0.4s cubic-bezier(0.4, 0, 0, 1.7);
    }
  `;

  // Zero-fill function
  const zerofill = (num) => (num < 10 && num >= 0 ? `0${num}` : num);

  // SvgCircle Component
  const SvgCircle = ({ className, done, max, radius, stroke, strokeWidth }) => {
    const size = (radius + strokeWidth) * 2;
    const length = Math.ceil(2 * radius * Math.PI);
    const remainingLength = length - Math.ceil(2 * radius * Math.PI) * (done / max);

    return (
      <svg className={className} width={size} height={size} viewBox={`0 0 ${size} ${size}`} xmlns="http://www.w3.org/2000/svg">
        <g>
          <circle
            className="circle"
            r={radius}
            cx={radius + strokeWidth}
            cy={radius + strokeWidth}
            stroke={stroke}
            strokeDasharray={length}
            strokeDashoffset={remainingLength}
            strokeLinecap="round"
            strokeWidth={strokeWidth}
            fill="none"
          />
          <circle
            className="circle--bg"
            r={radius}
            cx={radius + strokeWidth}
            cy={radius + strokeWidth}
            stroke="rgba(0, 0, 0, .1)"
            strokeLinecap="round"
            strokeWidth={strokeWidth}
            fill="none"
          />
        </g>
      </svg>
    );
  };

  SvgCircle.defaultProps = {
    radius: props.radius ? props.radius : 60,
    stroke: props.stroke ? props.stroke : theme.palette.primary.main,
    strokeWidth: props.strokeWidth ? props.strokeWidth : 10,
  };

  // Clock Component
  const Clock = ({ deadline }) => {
    const [time, setTime] = useState({
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    });

    useEffect(() => {
      const timerId = setInterval(() => getTimeUntil(deadline), 1000);

      return () => clearInterval(timerId);
    }, [deadline]);

    const getTimeUntil = (dline: string) => {
      const clockTime = Date.parse(dline) - Date.parse(new Date().toISOString());
      const seconds = Math.floor((clockTime / 1000) % 60);
      const minutes = Math.floor((clockTime / 1000 / 60) % 60);
      const hours = Math.floor((clockTime / (1000 * 60 * 60)) % 24);
      const days = Math.floor(clockTime / (1000 * 60 * 60 * 24));

      // Check if past date
      if (clockTime < 0) {
        setTime({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
        return;
      }

      setTime({ days, hours, minutes, seconds });
    };

    const timeAllZero = time.days === 0 && time.hours === 0 && time.minutes === 0 && time.seconds === 0;

    return (
      <div className="clock">
        {!props.disableDays && (
          <div className="clock__display">
            <SvgCircle className="clock__circle" max={365} done={time.hours} />
            <div className="clock__text clock__text--days">
              <span className="clock__amount">{timeAllZero ? "-" : zerofill(time.days)}</span>
              <span className="clock__unit">{props.labels.days}</span>
            </div>
          </div>
        )}
        {!props.disableHours && (
          <div className="clock__display">
            <SvgCircle className="circle" max={24} done={time.hours} />
            <div className="clock__text clock__text--hours">
              <span className="clock__amount">{timeAllZero ? "-" : zerofill(time.hours)}</span>
              <span className="clock__unit">{props.labels.hours}</span>
            </div>
          </div>
        )}
        {!props.disableMinutes && (
          <div className="clock__display">
            <SvgCircle className="circle" max={60} done={time.minutes} />
            <div className="clock__text clock__text--minutes">
              <span className="clock__amount">{timeAllZero ? "-" : zerofill(time.minutes)}</span>
              <span className="clock__unit">{props.labels.minutes}</span>
            </div>
          </div>
        )}
        {!props.disableSeconds && (
          <div className="clock__display">
            <SvgCircle className="circle" max={60} done={time.seconds} />
            <div className="clock__text clock__text--seconds">
              <span className="clock__amount">{timeAllZero ? "-" : zerofill(time.seconds)}</span>
              <span className="clock__unit">{props.labels.seconds}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Container>
      <Clock deadline={props.deadline} />
    </Container>
  );
};

export default WcDateCountDown;
